import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { Download, Printer, ArrowLeft, Crown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import { useAuth } from "../contexts/AuthContext";

export default function PalletDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pallet, setPallet] = useState(null);

  useEffect(() => {
    api.get(`/pallets/${id}`).then((r) => setPallet(r.data)).catch(() => setPallet(false));
  }, [id]);

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    try {
      const res = await api.get(`/pallets/${id}/label`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pallet-${pallet.pallet_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download label PDF");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this pallet? This cannot be undone.")) return;
    try {
      await api.delete(`/pallets/${id}`);
      toast.success("Pallet deleted");
      navigate("/inventory");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (pallet === null) {
    return (
      <div className="p-10 text-zinc-500 text-sm uppercase tracking-widest">
        Loading…
      </div>
    );
  }
  if (pallet === false) {
    return <div className="p-10">Pallet not found.</div>;
  }

  const canManage = user?.role === "admin" || user?.role === "operator";

  return (
    <div data-testid="pallet-detail-page">
      <PageHeader
        eyebrow="Pallet record"
        title={pallet.pallet_number}
        action={
          <>
            <Link to="/inventory" className="mj-btn mj-btn-ghost" data-testid="back-to-inventory">
              <ArrowLeft size={14} /> Inventory
            </Link>
            <button onClick={handleDownloadPDF} className="mj-btn mj-btn-secondary" data-testid="download-label-btn">
              <Download size={14} /> 4×6 PDF
            </button>
            <button onClick={handlePrint} className="mj-btn mj-btn-primary" data-testid="print-label-btn">
              <Printer size={14} /> Print Label
            </button>
            {user?.role === "admin" && (
              <button onClick={handleDelete} className="mj-btn mj-btn-ghost" style={{color: "var(--mj-red)"}} data-testid="delete-pallet-btn">
                <Trash2 size={14} /> Delete
              </button>
            )}
          </>
        }
      />

      <div className="p-6 md:p-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mj-card space-y-6" data-testid="pallet-info-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="data-label">Status</div>
              <div className="mt-2"><StatusPill status={pallet.status} /></div>
            </div>
            <div className="text-right">
              <div className="data-label">Logged by</div>
              <div className="text-sm mt-1">{pallet.created_by_name}</div>
              <div className="text-xs text-[var(--mj-ink-muted)] font-mono-id">
                {new Date(pallet.created_at).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="data-label mb-2">Product</div>
              <div className="text-xl font-display font-bold">{pallet.product_name}</div>
              <div className="text-sm text-[var(--mj-ink-muted)] font-mono-id">
                {pallet.product_sku} · Lot {pallet.lot_number}
              </div>
            </div>
            <div>
              <div className="data-label mb-2">Quantity</div>
              <div className="text-xl font-display font-bold">{pallet.quantity} <span className="text-base text-[var(--mj-ink-muted)] font-sans">{pallet.units_label}</span></div>
            </div>
            <div>
              <div className="data-label mb-2">Produced</div>
              <div className="text-xl font-mono-id font-semibold">{pallet.production_date}</div>
            </div>
            <div>
              <div className="data-label mb-2">Expires</div>
              <div className="text-xl font-mono-id font-semibold" style={{ color: pallet.status === "expired" ? "var(--mj-red)" : undefined }}>
                {pallet.expiry_date}
              </div>
            </div>
            <div>
              <div className="data-label mb-2">Destination customer</div>
              <div className="text-xl font-display font-bold">{pallet.customer_name || "—"}</div>
            </div>
            <div>
              <div className="data-label mb-2">Pallet ID (barcode)</div>
              <div className="text-xl font-mono-id font-bold">{pallet.pallet_number}</div>
            </div>
          </div>
          {pallet.notes && (
            <div>
              <div className="data-label mb-2">Notes</div>
              <div className="text-sm">{pallet.notes}</div>
            </div>
          )}
        </div>

        <div className="mj-card" data-testid="barcode-card">
          <div className="data-label mb-3">Scan to open</div>
          <div
            className="flex flex-col items-center p-4"
            style={{ background: "#ffffff", border: "1px solid var(--mj-border)" }}
          >
            <QRCodeSVG
              value={`${window.location.origin}/p/${pallet.barcode || pallet.pallet_number.replace(/-/g, "")}`}
              size={180}
              level="M"
              includeMargin
              data-testid="pallet-qr-code"
            />
            <p className="text-[11px] text-[var(--mj-ink-muted)] mt-2 text-center leading-tight">
              Point any phone camera at this QR code to open the pallet record.
            </p>
          </div>

          <div className="data-label mt-5 mb-3">Code128 (warehouse scanner)</div>
          <div className="barcode-wrap p-3 border border-[var(--mj-border)]">
            <Barcode
              value={pallet.barcode || pallet.pallet_number.replace(/-/g, "")}
              format="CODE128"
              displayValue={true}
              fontOptions="bold"
              textMargin={6}
              width={3}
              height={120}
              margin={25}
              background="#ffffff"
              lineColor="#000000"
            />
          </div>
          <div className="mt-3 text-center">
            <div className="data-label">Pallet # (human readable)</div>
            <div className="font-mono-id text-base font-bold mt-1">
              {pallet.pallet_number}
            </div>
          </div>
          <p className="text-xs text-[var(--mj-ink-muted)] mt-3 leading-relaxed">
            Phones use the QR code; USB Code128 scanners use the bars above. Both resolve to this pallet's production record. Use the buttons up top to print the 4×6 thermal label.
          </p>
        </div>
      </div>

      {/* Hidden print-only label */}
      <div className="print-label" style={{ display: "none" }} data-testid="print-label-area">
        <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
          <div className="flex items-center gap-2">
            <div style={{ background: "var(--mj-red)", padding: 4 }}>
              <Crown size={16} color="#E5B13A" strokeWidth={2.5} />
            </div>
            <span className="font-display text-xl font-black">MAJESTY FOODS</span>
          </div>
          <span className="text-xs font-bold uppercase tracking-widest">Pallet Label</span>
        </div>
        <div className="data-label">Pallet #</div>
        <div className="font-mono-id text-2xl font-bold mb-3">{pallet.pallet_number}</div>
        <div className="data-label">Product</div>
        <div className="font-display text-lg font-bold leading-tight">{pallet.product_name}</div>
        <div className="text-xs font-mono-id mb-3">{pallet.product_sku} · Lot {pallet.lot_number}</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="data-label">Produced</div>
            <div className="font-mono-id text-base font-bold">{pallet.production_date}</div>
          </div>
          <div>
            <div className="data-label">Expires</div>
            <div className="font-mono-id text-base font-bold" style={{ color: "var(--mj-red)" }}>{pallet.expiry_date}</div>
          </div>
          <div>
            <div className="data-label">Quantity</div>
            <div className="font-mono-id text-base font-bold">{pallet.quantity} {pallet.units_label}</div>
          </div>
          <div>
            <div className="data-label">Destination</div>
            <div className="font-bold text-base">{pallet.customer_name || "—"}</div>
          </div>
        </div>
        <div className="flex flex-col items-center my-2" style={{ background: "#fff" }}>
          <div className="barcode-wrap" style={{ maxWidth: "100%" }}>
            <Barcode
              value={pallet.barcode || pallet.pallet_number.replace(/-/g, "")}
              format="CODE128"
              displayValue
              width={3}
              height={120}
              margin={25}
              background="#ffffff"
              lineColor="#000000"
            />
          </div>
          <QRCodeSVG
            value={`${window.location.origin}/p/${pallet.barcode || pallet.pallet_number.replace(/-/g, "")}`}
            size={120}
            level="M"
            includeMargin
            style={{ marginTop: 8 }}
          />
        </div>
      </div>

      <style>{`@media print { .print-label { display: block !important; } }`}</style>
    </div>
  );
}
